import { Box, type BoxProps, Typography, type TypographyProps } from '@mui/material'
import type { ReactNode } from 'react'
import { STATUS_COLOURS, tokens, type PlotStatus } from '../theme'
import type { Tier } from '../api/types'

/** Uppercase overline — the only place the design system allows caps. */
export function Eyebrow({ children, sx, ...rest }: TypographyProps) {
  return (
    <Typography variant="overline" component="div" sx={{ color: 'text.disabled', ...sx }} {...rest}>
      {children}
    </Typography>
  )
}

/** IDs, filenames, coordinates and EPSG codes. */
export function Mono({ children, sx, ...rest }: BoxProps) {
  return (
    <Box
      component="span"
      sx={{ fontFamily: tokens.font.mono, fontVariantNumeric: 'tabular-nums', ...sx }}
      {...rest}
    >
      {children}
    </Box>
  )
}

/** Any figure, always with tabular numerals. */
export function Num({ children, sx, ...rest }: BoxProps) {
  return (
    <Box component="span" sx={{ fontVariantNumeric: 'tabular-nums', ...sx }} {...rest}>
      {children}
    </Box>
  )
}

const STATUS_LABEL: Record<PlotStatus, string> = {
  passed: 'Passed',
  flagged: 'Flagged',
  rejected: 'Rejected',
  excluded: 'Excluded',
}

export function StatusPill({ status, label }: { status: PlotStatus; label?: string }) {
  const c = STATUS_COLOURS[status]
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex', alignItems: 'center', height: 20, px: 2,
        borderRadius: tokens.radius.pill, backgroundColor: c.tint, color: c.text,
        font: `500 11px ${tokens.font.sans}`, whiteSpace: 'nowrap',
      }}
    >
      {label ?? STATUS_LABEL[status]}
    </Box>
  )
}

export function TierPill({ tier }: { tier: Tier }) {
  const compliance = tier === 'compliance'
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex', alignItems: 'center', height: 20, px: 2,
        borderRadius: tokens.radius.pill,
        backgroundColor: compliance ? tokens.color.tintDanger : tokens.color.tintWarning,
        color: compliance ? tokens.color.danger : tokens.color.warning,
        font: `500 11px ${tokens.font.sans}`, letterSpacing: '0.08em', textTransform: 'uppercase',
        whiteSpace: 'nowrap',
      }}
    >
      {compliance ? 'Compliance' : 'Data quality'}
    </Box>
  )
}

/** The teal "EUDR" tag the mockups put next to the deforestation check. */
export function EudrTag() {
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex', alignItems: 'center', height: 16, px: 1.5,
        borderRadius: tokens.radius.pill, backgroundColor: tokens.color.brandTeal100,
        color: tokens.color.brandTeal, font: `500 10px ${tokens.font.sans}`, letterSpacing: '0.06em',
      }}
    >
      EUDR
    </Box>
  )
}

export function RepairableTag() {
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex', alignItems: 'center', height: 18, px: 1.5,
        borderRadius: tokens.radius.pill, backgroundColor: tokens.color.brandTeal100,
        color: tokens.color.brandTeal, font: `500 11px ${tokens.font.sans}`,
      }}
    >
      repairable
    </Box>
  )
}

/** The thin share bar in the 1c table. */
export function ShareBar({ value, tone }: { value: number; tone: 'compliance' | 'data_quality' }) {
  return (
    <Box sx={{ height: 6, width: '100%', borderRadius: tokens.radius.pill, backgroundColor: tokens.color.ink100, overflow: 'hidden' }}>
      <Box
        sx={{
          height: '100%',
          width: `${Math.max(value > 0 ? 2 : 0, Math.min(100, value))}%`,
          backgroundColor: tone === 'compliance' ? tokens.color.dataRisk.high : tokens.color.dataRisk.med,
          transition: `width ${tokens.motion.dur.base}ms ${tokens.motion.easeStd}`,
        }}
      />
    </Box>
  )
}

/** Passed / flagged / rejected, as one 6 px bar. */
export function StackedBar({
  passed, flagged, rejected, excluded = 0, total,
}: { passed: number; flagged: number; rejected: number; excluded?: number; total: number }) {
  const w = (n: number) => (total ? (n / total) * 100 : 0)
  return (
    <Box sx={{ display: 'flex', height: 6, borderRadius: tokens.radius.pill, overflow: 'hidden', backgroundColor: tokens.color.ink100 }}>
      <Box sx={{ width: `${w(passed)}%`, backgroundColor: tokens.color.brandGreen }} />
      <Box sx={{ width: `${w(flagged)}%`, backgroundColor: tokens.color.dataRisk.med }} />
      <Box sx={{ width: `${w(rejected)}%`, backgroundColor: tokens.color.dataRisk.high }} />
      <Box sx={{ width: `${w(excluded)}%`, backgroundColor: tokens.color.ink300 }} />
    </Box>
  )
}

/** Flat surface with a hairline border — the only card style in the system. */
export function Surface({ children, sx, ...rest }: BoxProps) {
  return (
    <Box
      sx={{
        backgroundColor: 'background.paper',
        border: `1px solid ${tokens.color.ink200}`,
        borderRadius: `${tokens.radius.md}px`,
        ...sx,
      }}
      {...rest}
    >
      {children}
    </Box>
  )
}

export function FieldLabel({ children }: { children: ReactNode }) {
  return (
    <Typography sx={{ font: `500 13px ${tokens.font.sans}`, color: 'text.primary', mb: 1.5 }}>
      {children}
    </Typography>
  )
}

/** Empty states name what will happen, not what is missing. */
export function EmptyState({ title, body, action }: { title: string; body: string; action?: ReactNode }) {
  return (
    <Box sx={{ textAlign: 'center', py: 16, px: 6, color: 'text.secondary' }}>
      <Typography variant="h4" sx={{ color: 'text.primary', mb: 2 }}>{title}</Typography>
      <Typography variant="body2" sx={{ maxWidth: 440, mx: 'auto', mb: action ? 5 : 0 }}>{body}</Typography>
      {action}
    </Box>
  )
}

/**
 * A count that is only worth reading when it is not zero — the number of a
 * producer's fields over the yield threshold.
 *
 * Same geometry as StatusPill, so a list mixing the two stays on the grid.
 */
export function CountChip({ value }: { value: number }) {
  const hot = value > 0
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        minWidth: 24, height: 20, px: 1.5, flex: 'none',
        borderRadius: tokens.radius.pill,
        backgroundColor: hot ? tokens.color.tintWarning : tokens.color.ink100,
        color: hot ? tokens.color.warning : tokens.color.ink500,
        font: `500 11px ${tokens.font.sans}`, fontVariantNumeric: 'tabular-nums',
      }}
    >
      {value}
    </Box>
  )
}
